// src/hooks/useCaseDetail.js
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { database, storage } from "../firebase";
import {
  ref as dbRef,
  set,
  update,
  query,
  orderByChild,
  equalTo,
  get,
} from "firebase/database";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { setSelectedCase, setSelectedCaseDetail } from "../redux/casesRedux";
import { Statuses } from "../constants/statuses";

export default function useCaseDetail(
  setShowSTLModal,
  setShowDICOMModal,
  setShowCommentModal,
  setSelectedComment
) {
  const dispatch = useDispatch();
  const selectedCase = useSelector((state) => state.cases.selectedCase);
  const selectedCaseDetail = useSelector(
    (state) => state.cases.selectedCaseDetail
  );
  const user = useSelector((state) => state.user);
  const caseId = selectedCase?.id;

  const [progress, setProgress] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  // helpers
  const updateLocalCase = (updates) => {
    const merged = { ...selectedCase, ...updates };
    dispatch(setSelectedCase(merged));
  };

  const updateLocalCaseDetail = (updates) => {
    const merged = { ...selectedCaseDetail, ...updates };
    dispatch(setSelectedCaseDetail(merged));
  };

  const normalizeEmail = (e) => (e || "").trim().toLowerCase();

  // Upload STL files and update database
  const handleSTLUpload = async ({
    files,
    titles,
    orderNumbers,
    revisionNumbers,
    colorList,
    caseReady,
  }) => {
    if (!caseId) return;
    setUploading(true);
    const currentUser = user.currentUser || {};
    const newInfos = [...(selectedCaseDetail?.modelInfos || [])];

    const uploads = files.map(
      (file, index) =>
        new Promise((resolve, reject) => {
          const uniqueName = `${titles[index]}_${Date.now()}.stl`;
          const uploadRef = storageRef(
            storage,
            `cases/${caseId}/stlFiles/${uniqueName}`
          );
          const task = uploadBytesResumable(uploadRef, file);

          task.on(
            "state_changed",
            (snapshot) => {
              const pct =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setProgress((prev) => {
                const arr = [...prev];
                arr[index] = pct;
                return arr;
              });
            },
            (err) => reject(err),
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              const info = {
                name: titles[index] || file.name,
                url,
                uploadedBy: `${currentUser.firstName || "Unknown"} ${
                  currentUser.lastName || "User"
                }`,
                uploadDate: Date.now(),
                storageName: uniqueName,
                orderNumber: orderNumbers[index],
                revisionNumber: revisionNumbers[index],
                color: colorList[index],
              };
              newInfos.push(info);
              resolve(info);
            }
          );
        })
    );

    await Promise.all(uploads);
    await set(dbRef(database, `caseDetails/${caseId}/modelInfos`), newInfos);

    if (caseReady) {
      await update(dbRef(database, `cases/${caseId}`), {
        status: Statuses.READY,
      });
      updateLocalCase({ status: Statuses.READY });
    }

    updateLocalCaseDetail({ modelInfos: newInfos });
    setUploading(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
    setShowSTLModal(false);
    setProgress([]);
  };

  // Upload DICOM file and update status
  const handleDICOMUpload = async ({ files }) => {
    if (!caseId) return;
    setUploading(true);
    setProgress(new Array(files.length).fill(0));
    const currentUser = user.currentUser || {};
    const existing = selectedCaseDetail?.dicoms
      ? selectedCaseDetail.dicoms.map((d) => ({
          ...d,
          isSelected: false,
        }))
      : [];
    const newDicoms = [...existing];

    const uploads = files.map(
      (file, i) =>
        new Promise((resolve, reject) => {
          const uniqueName = `${file.name}_${Date.now()}_${i}`;
          const ref = storageRef(
            storage,
            `cases/${caseId}/dicomFiles/${uniqueName}`
          );
          const task = uploadBytesResumable(ref, file);

          task.on(
            "state_changed",
            (snapshot) => {
              const pct =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setProgress((prev) => {
                const arr = [...prev];
                arr[i] = pct;
                return arr;
              });
            },
            (err) => reject(err),
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              const dicom = {
                name: file.name,
                url,
                uploadedBy: `${currentUser.firstName || "Unknown"} ${
                  currentUser.lastName || "User"
                }`,
                uploadDate: Date.now(),
                storageName: uniqueName,
                isSelected: i === 0,
              };
              newDicoms.push(dicom);
              resolve(dicom);
            }
          );
        })
    );

    await Promise.all(uploads);
    await set(dbRef(database, `caseDetails/${caseId}/dicoms`), newDicoms);
    await update(dbRef(database, `cases/${caseId}`), {
      status: Statuses.IN_PROGRESS,
    });

    updateLocalCase({ status: Statuses.IN_PROGRESS });
    updateLocalCaseDetail({ dicoms: newDicoms });

    setUploading(false);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setProgress([]);
      setShowDICOMModal(false);
    }, 1000);
  };

  // Delete an STL model and its storage object
  const handleDeleteModel = async (index) => {
    if (!caseId) return;
    const infos = [...(selectedCaseDetail?.modelInfos || [])];
    const [removed] = infos.splice(index, 1);
    await set(dbRef(database, `caseDetails/${caseId}/modelInfos`), infos);

    if (removed?.storageName) {
      await deleteObject(
        storageRef(storage, `cases/${caseId}/stlFiles/${removed.storageName}`)
      );
    }

    updateLocalCaseDetail({ modelInfos: infos });
  };

  // Select a DICOM file
  const handleSelectDicom = async (index) => {
    if (!caseId) return;
    const dicoms = selectedCaseDetail?.dicoms || [];
    if (dicoms[index]?.isSelected) return;
    const updated = dicoms.map((d, i) => ({ ...d, isSelected: i === index }));
    await set(dbRef(database, `caseDetails/${caseId}/dicoms`), updated);
    updateLocalCaseDetail({ dicoms: updated });
  };

  // Delete a DICOM entry
  const handleDeleteDicom = async (index) => {
    if (!caseId) return;

    const dicoms = (selectedCaseDetail?.dicoms || []).map((d) => ({
      ...d,
    }));
    const [removed] = dicoms.splice(index, 1);

    //TODO This is a security bridge. One can delete the dicom files and model files in storage even though realtime database is safe.
    if (removed?.storageName) {
      await deleteObject(
        storageRef(storage, `cases/${caseId}/dicomFiles/${removed.storageName}`)
      );
    }

    if (removed.isSelected && dicoms.length) {
      dicoms[0].isSelected = true;
    }

    await set(dbRef(database, `caseDetails/${caseId}/dicoms`), dicoms);
    updateLocalCaseDetail({ dicoms });
  };

  // Add or edit a comment
  const handleCommentSubmit = async ({ text, timestamp }) => {
    if (!caseId) return;
    const ts = timestamp || Date.now();
    const detailRef = dbRef(database, `caseDetails/${caseId}/comments/${ts}`);
    const currentUser = user.currentUser || {};
    const payload = {
      text,
      author: `${currentUser.firstName || "Unknown"} ${
        currentUser.lastName || "User"
      }`,
      authorId: currentUser.id,
    };
    if (timestamp) payload.editTimestamp = Date.now();
    await set(detailRef, payload);

    // Update only the caseDetail local slice
    updateLocalCaseDetail({
      comments: { ...(selectedCaseDetail?.comments || {}), [ts]: payload },
    });

    setSelectedComment(null);
    setShowCommentModal(false);
  };

  // Delete a comment
  const handleDeleteComment = async (timestamp) => {
    if (!caseId) return;
    // Remove comment from caseDetails only
    await set(
      dbRef(database, `caseDetails/${caseId}/comments/${timestamp}`),
      null
    );

    const updatedDetail = { ...(selectedCaseDetail?.comments || {}) };
    delete updatedDetail[timestamp];
    updateLocalCaseDetail({ comments: updatedDetail });
  };

  // Multi-share with per-person roles, mirroring single-share behavior (no invites)
  const handleShareAssignments = async ({ assignments }) => {
    if (!caseId) return [];
    const currentUser = user.currentUser || {};
    const results = [];

    // start from the latest users map and mutate locally
    const localUsers = { ...(selectedCase?.users || {}) };

    for (const a of assignments) {
      const email = normalizeEmail(a.email);
      const permission = a.role;

      // self guard
      if (email === normalizeEmail(currentUser.email)) {
        results.push({
          email,
          role: permission,
          status: "self",
          message: "You already have access.",
        });
        continue;
      }

      try {
        // lookup user by email
        const snap = await get(
          query(dbRef(database, "users"), orderByChild("email"), equalTo(email))
        );

        if (!snap.exists()) {
          results.push({
            email,
            role: permission,
            status: "not-found",
            message: "User not found",
          });
          continue;
        }

        const userId = Object.keys(snap.val())[0];
        const info = snap.val()[userId];

        // already member?
        if (localUsers[userId]) {
          results.push({ email, role: permission, status: "already-member" });
          continue;
        }

        // write membership (same as single-share)
        await update(dbRef(database, `cases/${caseId}/users/${userId}`), {
          role: permission,
          name: `${info.firstName} ${info.lastName}`,
        });

        // update user's cases map if needed
        const prevCasesMap = info.cases || {};
        const caseExists = Object.values(prevCasesMap).includes(caseId);
        if (!caseExists) {
          const updatedCasesMap = {
            ...prevCasesMap,
            [Date.now()]: caseId,
          };
          await set(dbRef(database, `users/${userId}/cases`), updatedCasesMap);
        }

        // accumulate locally (don't dispatch yet)
        localUsers[userId] = {
          role: permission,
          name: `${info.firstName} ${info.lastName}`,
        };

        results.push({ email, role: permission, status: "member-added" });
      } catch (err) {
        results.push({
          email,
          role: a.role,
          status: "error",
          message: err?.message || "Unknown error",
        });
      }
    }

    // single Redux update to avoid overwriting earlier loop updates
    if (
      Object.keys(localUsers).length !==
      Object.keys(selectedCase?.users || {}).length
    ) {
      updateLocalCase({ users: localUsers });
    }

    return results;
  };

  // Update user roles in case
  const handleRoleChange = async (users) => {
    if (!caseId) return;
    await set(dbRef(database, `cases/${caseId}/users`), users);
    updateLocalCase({ users: users });
  };

  // Remove a user from case
  const handleDeleteUser = async (userId) => {
    if (!caseId) return;
    try {
      const userSnapshot = await get(dbRef(database, `users/${userId}`));
      const userData = userSnapshot.val();

      if (!userData) {
        console.error("User not found");
        return;
      }

      // Remove case from user's cases map
      const casesMap = userData.cases || {};
      const updatedCasesMap = {};
      Object.entries(casesMap).forEach(([timestamp, id]) => {
        if (id !== caseId) {
          updatedCasesMap[timestamp] = id;
        }
      });

      const roles = { ...selectedCase.users };
      delete roles[userId];

      await set(dbRef(database, `users/${userId}/cases`), updatedCasesMap);
      await set(dbRef(database, `cases/${caseId}/users`), roles);
      updateLocalCase({ users: roles });
    } catch (error) {
      console.error("Error removing user from case:", error);
    }
  };

  // Save model groups
  const handleSaveModelGroups = async (groups, applyToAllUsers) => {
    if (!caseId) return;
    const currentUserId = user.currentUser?.id;
    if (!currentUserId) return;

    // Filter out empty groups
    const nonEmptyGroups = groups.filter(
      (g) => g.indices && g.indices.length > 0
    );

    // Determine the key: 'common' or userId
    const targetKey = applyToAllUsers ? "common" : currentUserId;

    try {
      // Save to caseDetails/[caseId]/modelGroups/[targetKey]
      await set(
        dbRef(database, `caseDetails/${caseId}/modelGroups/${targetKey}`),
        nonEmptyGroups.length > 0 ? nonEmptyGroups : null
      );

      // Update local state
      const updatedGroups = {
        ...(selectedCaseDetail?.modelGroups || {}),
        [targetKey]: nonEmptyGroups.length > 0 ? nonEmptyGroups : null,
      };

      // Remove null entries
      if (updatedGroups[targetKey] === null) {
        delete updatedGroups[targetKey];
      }

      updateLocalCaseDetail({ modelGroups: updatedGroups });
    } catch (error) {
      console.error("Error saving model groups:", error);
      throw error;
    }
  };

  return {
    progress,
    uploading,
    success,
    handleSTLUpload,
    handleDICOMUpload,
    handleDeleteModel,
    handleSelectDicom,
    handleDeleteDicom,
    handleCommentSubmit,
    handleDeleteComment,
    handleShareAssignments,
    handleRoleChange,
    handleDeleteUser,
    handleSaveModelGroups,
  };
}
